import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { PageShell } from './layout';
import { useGettoncini } from '../contexts/GettonciniContext';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import Button from './Button';
import FaIcon from './FaIcon';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import styles from '../styles/GettonciniLandingPage.module.css';
import buttonStyles from '../styles/Button.module.css';

// Custom large GettonciniIcon for hero with unique ID to avoid SVG clipPath conflicts
const HeroGettonciniIcon = ({ size = 120, style = {}, className = '' }) => {
  const uniqueId = React.useId();
  return (
    <svg 
      className={className}
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      style={style}
    >
      <defs>
        <clipPath id={`gettoncini-hero-clip-${uniqueId}`}>
          <circle cx="12" cy="12" r="10"/>
        </clipPath>
        <linearGradient id={`gettoncini-hero-gradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>
        <filter id={`gettoncini-hero-glow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <circle 
        cx="12" 
        cy="12" 
        r="10" 
        fill={`url(#gettoncini-hero-gradient-${uniqueId})`} 
        stroke="#1e40af" 
        strokeWidth="1"
        filter={`url(#gettoncini-hero-glow-${uniqueId})`}
      />
      <g clipPath={`url(#gettoncini-hero-clip-${uniqueId})`}>
        <circle cx="12" cy="12" r="7" fill="#60a5fa" opacity="0.25"/>
        <text 
          x="12" 
          y="10.5" 
          fontSize="25" 
          fill="#dbeafe" 
          fontFamily="'Corinthia', cursive"
          fontWeight="700"
          textAnchor="middle" 
          dominantBaseline="central"
        >
          g
        </text>
      </g>
    </svg>
  );
};

// How to earn data
const EARNING_METHODS = [
  {
    icon: 'fa-user-plus',
    title: 'Crea un account',
    description: 'Registrati gratuitamente e ricevi subito i tuoi primi 1.000 gettoncini di benvenuto.',
    amount: 1000,
    gradient: 'linear-gradient(145deg, #10b981, #059669)'
  },
  {
    icon: 'fa-envelope-open-text',
    title: 'Iscriviti alla newsletter',
    description: 'Rimani aggiornato sulle novità e guadagna gettoncini bonus.',
    amount: 500,
    gradient: 'linear-gradient(145deg, #f59e0b, #d97706)'
  },
  {
    icon: 'fa-circle-play',
    title: 'Guarda un annuncio premio',
    description: 'Quando disponibile, puoi ricevere gettoncini extra guardando un breve annuncio.',
    amount: 50,
    gradient: 'linear-gradient(145deg, #06b6d4, #0f766e)'
  }
];

// How to spend data
const SPENDING_METHODS = [
  {
    icon: 'fa-lightbulb',
    title: 'Suggerimenti',
    description: "Usa i gettoncini per sbloccare funzioni premium che arricchiscono la tua esperienza d'apprendimento.",
    emoji: '💡'
  },
  {
    icon: 'fa-wand-magic-sparkles',
    title: 'Rivelazioni',
    description: 'Scopri lettere, temi o elementi nascosti per proseguire nel gioco.',
    emoji: '✨'
  },
  {
    icon: 'fa-robot',
    title: 'Strumenti AI',
    description: 'Accedi a funzionalità avanzate per approfondire parole e concetti.',
    emoji: '🤖'
  }
];

const GettonciniLandingPage = () => {
  const { isSignedIn } = useUser();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  const { balance } = useGettoncini();
  const earningMethods = isSignedIn
    ? EARNING_METHODS
    : EARNING_METHODS.filter((method) => method.title !== 'Guarda un annuncio premio');
  
  const displayBalance = isSignedIn ? balance : 0;

  // Navigate to sign-in/sign-up page
  const handleAuthClick = () => {
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: hasEverAuthenticated ? 'sign_in' : 'sign_up',
      source_surface: 'gettoncini_landing',
      gate_reason: 'gettoncini_landing',
      next_path: '/gettoncini',
      is_returning_device: hasEverAuthenticated,
      track_gate_viewed: true,
      trigger: 'landing_cta',
    });
    navigate(authUrl);
  };

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Floating coins background */}
        <div className={styles.floatingCoins}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className={styles.floatingCoin}>
              <HeroGettonciniIcon size={30 + (i % 3) * 15} />
            </div>
          ))}
        </div>

        {/* Sparkles */}
        <div className={styles.sparkles}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className={styles.sparkle} />
          ))}
        </div>

        <div className={styles.heroContent}>
          {/* Main coin icon */}
          <div className={styles.iconShowcase}>
            <div className={styles.mainIcon}>
              <div className={styles.spinWrapper}>
                <HeroGettonciniIcon size={120} />
              </div>
            </div>
          </div>

          <h1 className={styles.heroTitle}>Gettoncini</h1>
          
          <p className={styles.heroSubtitle}>
            La valuta di Giochini di Parole. Lo strumento perfetto per un apprendimento di qualità.
          </p>

          {isSignedIn ? (
            <div className={styles.balanceCard}>
              <span className={styles.balanceLabel}>Il tuo saldo attuale</span>
              <div className={styles.balanceValue}>
                <span className={styles.balanceNumber}>{displayBalance.toLocaleString('it-IT')}</span>
                <HeroGettonciniIcon size={36} />
              </div>
              <Link to="/" className={styles.playButton}>
                <FaIcon className="fas fa-gamepad"  />
                <span>Vai a giocare</span>
              </Link>
              <div className={styles.rewardedCardWrap}>
                <p className={styles.rewardedCardText}>
                  Guarda un breve annuncio per ottenere una ricompensa.
                </p>
                <RewardedGettonciniCard
                  sourceSurface="gettoncini_landing"
                  authNextPath="/gettoncini"
                  signedOutGateReason="gettoncini_rewarded_ads_requires_auth"
                  signedOutMessage="Accedi per attivare gli annunci premio dalla pagina gettoncini."
                />
              </div>
            </div>
          ) : (
            <div className={styles.ctaContainer}>
              <Button 
                primary 
                className={`${styles.ctaButton} ${buttonStyles.primaryBlue}`}
                onClick={handleAuthClick}
              >
                {hasEverAuthenticated ? 'Accedi per vedere il tuo saldo' : 'Inizia a guadagnare'}
              </Button>
              <p className={styles.ctaNote}>
                Registrazione gratuita • Niente spam
              </p>
            </div>
          )}
        </div>

        <div className={styles.scrollIndicator}>
          <span>Scopri di più</span>
          <FaIcon className={`fas fa-chevron-down ${styles.scrollArrow}`}  />
        </div>
      </section>

      {/* What are Gettoncini Section */}
      <section className={styles.whatSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Cosa sono i gettoncini?</h2>
          <p className={styles.sectionDescription}>
            I gettoncini sono la valuta virtuale di Giochini di Parole. 
            Non costano nulla: te li regaliamo noi al tuo primo accesso quotidiano. Puoi usarli per sbloccare aiutini, 
            quando ti trovi in difficoltà durante una partita, e per imparare con Lella, il nostro assistente IA.
          </p>
          
          <div className={styles.keyPoints}>
            <div className={styles.keyPoint}>
              <div className={styles.keyPointIcon}>
                <FaIcon className="fas fa-gift"  />
              </div>
              <h3>100% Gratuiti</h3>
              <p>Non si comprano, te li regaliamo noi.</p>
            </div>
            <div className={styles.keyPoint}>
              <div className={styles.keyPointIcon}>
                <FaIcon className="fas fa-clock"  />
              </div>
              <h3>A scadenza</h3>
              <p>Al primo accesso quotidiano ricevi 250 gettoncini, validi per un'ora. Ogni 8 ore puoi ricevere altri 250 gettoncini.</p>
            </div>
            <div className={styles.keyPoint}>
              <div className={styles.keyPointIcon}>
                <FaIcon className="fas fa-lock"  />
              </div>
              <h3>Personali</h3>
              <p>Legati al tuo account, non trasferibili. Tutti i gettoncini che non usi scompaiono al termine dell'ora di gioco.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Earn Section */}
      <section className={styles.earnSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>📈</span>
            <h2 className={styles.sectionTitle}>Come guadagnare</h2>
            <p className={styles.sectionSubtitle}>
              {`Ci sono ${earningMethods.length} modi per guadagnare gettoncini.`}
            </p>
          </div>

          <div className={styles.earningGrid}>
            {earningMethods.map((method, index) => (
              <div key={index} className={styles.earningCard}>
                <div 
                  className={styles.earningIconWrapper}
                  style={{ background: method.gradient }}
                >
                  <FaIcon className={`fas ${method.icon}`} />
                </div>
                <div className={styles.earningContent}>
                  <h3 className={styles.earningTitle}>{method.title}</h3>
                  <p className={styles.earningDescription}>{method.description}</p>
                </div>
                {typeof method.amount === 'number' && (
                  <div className={styles.earningAmount}>
                    <span className={styles.amountPrefix}>+</span>
                    <span className={styles.amountValue}>{method.amount}</span>
                    <HeroGettonciniIcon size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Spend Section */}
      <section className={styles.spendSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>🎯</span>
            <h2 className={styles.sectionTitle}>Come spendere</h2>
            <p className={styles.sectionSubtitle}>
              Usa i gettoncini quando hai bisogno di una mano durante le partite.
            </p>
          </div>

          <div className={styles.spendingGrid}>
            {SPENDING_METHODS.map((method, index) => (
              <div key={index} className={styles.spendingCard}>
                <span className={styles.spendingEmoji}>{method.emoji}</span>
                <h3 className={styles.spendingTitle}>{method.title}</h3>
                <p className={styles.spendingDescription}>{method.description}</p>
              </div>
            ))}
          </div>

          <div className={styles.spendingNote}>
            <FaIcon className="fas fa-info-circle"  />
            <p>
              Ogni gioco ha i suoi aiutini specifici. Consulta le istruzioni 
              di ogni gioco per scoprire quali suggerimenti sono previsti 
              e quanto costano.
            </p>
          </div>
        </div>
      </section>

      {/* Lella AI Highlight Section */}
      <section className={styles.lellaSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.lellaCard}>
            <div className={styles.lellaImageCol}>
              <img src="/lella.png" alt="Lella" className={styles.lellaAvatar} />
            </div>
            <div className={styles.lellaTextCol}>
              <span className={styles.lellaBadge}>
                <FaIcon className="fa-solid fa-sparkles"  />
                Novità
              </span>
              <h2 className={styles.lellaTitle}>Scopri di più con Lella</h2>
              <p className={styles.lellaDescription}>
                Hai finito una partita e vuoi capire meglio una parola, un'espressione 
                o un modo di dire? Usa i tuoi gettoncini per chiedere a <strong>Lella</strong>, 
                la tua assistente AI. Ti spiegherà il significato in contesto, con esempi 
                pratici e curiosità linguistiche — tutto direttamente dentro il gioco.
              </p>
              <div className={styles.lellaCost}>
                <HeroGettonciniIcon size={22} />
                <span>Ogni richiesta usa i tuoi <strong>gettoncini</strong></span>
              </div>
              <Link to="/assistente-ai" className={styles.lellaLink}>
                <FaIcon className="fa-solid fa-sparkles"  />
                <span>Scopri come funziona Lella</span>
                <FaIcon className="fas fa-arrow-right"  />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Domande frequenti</h2>
          
          <div className={styles.faqGrid}>
            <div className={styles.faqItem}>
              <h3>Posso comprare i gettoncini?</h3>
              <p>
                No, i gettoncini non sono in vendita. Te li regaliamo noi al primo accesso quotidiano. Puoi ricevere gettoncini bonus se registri un account e ti iscrivi alla newsletter.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Cosa succede se finisco i gettoncini?</h3>
              <p>
                Puoi sempre continuare a giocare! Semplicemente non puoi 
                usare le funzioni premium fino a quando non ricevi altri gettoncini (ogni 8 ore).
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>Posso prestare i gettoncini?</h3>
              <p>
                No, i gettoncini sono personali e legati al tuo account. 
                Non possono essere prestati o venduti.
              </p>
            </div>
            <div className={styles.faqItem}>
              <h3>I gettoncini hanno un valore reale?</h3>
              <p>
                No, i gettoncini sono una valuta virtuale senza valore 
                monetario. Esistono solo all'interno di Giochini di Parole.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className={styles.bottomCta}>
        <div className={styles.bottomCtaContent}>
          <HeroGettonciniIcon size={80} className={styles.bottomCtaIcon} />
          <h2 className={styles.bottomCtaTitle}>
            {isSignedIn ? 'Torna a giocare!' : 'Pronto a iniziare?'}
          </h2>
          <p className={styles.bottomCtaText}>
            {isSignedIn 
              ? 'Usa i gettoncini per un apprendimento di qualità.'
              : 'Crea un account gratuito e ricevi subito i tuoi primi gettoncini.'
            }
          </p>
          
          {isSignedIn ? (
            <Link to="/" className={styles.bottomCtaButton}>
              <FaIcon className="fas fa-gamepad"  />
              <span>Vai ai giochi</span>
            </Link>
          ) : (
            <button className={styles.bottomCtaButton} onClick={handleAuthClick}>
              <FaIcon className="fas fa-user-plus"  />
              <span>{hasEverAuthenticated ? 'Accedi' : 'Registrati gratis'}</span>
            </button>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default GettonciniLandingPage;
